/* eslint-disable no-prototype-builtins */
import Button from '@components/atoms/button'
import BookFreeClassBtn from '@components/common/book-free-class-btn'
import { FastlyImage } from '@components/common/image'
import FixedMobileCTA from '@components/course-landing-page/fixed-mobile-cta'
import { COURSE_FIRST_FOLD, COURSE_TITLES } from '@lib/data/course-landing-page'
import { CourseNameType } from '@lib/types/course-landing-page'
import { useState } from 'react'
import { Download } from 'react-feather'
import Modal from '@components/common/modal'
import StudentCountriesOverview from '@components/home/student-countries-overview'
import Ratings from '@components/common/ratings'

const IMAGE_PATH = '/images/courses/'

type CourseCardProps = {
  shouldHideCardImage?: boolean
  courseName: CourseNameType
  ishideStarRating?: boolean
  ishideDownloadCurriculum?: boolean
  urlQuery?: string
  isHpePage?: boolean
  customPoints?: string
}

const CourseCard = ({
  shouldHideCardImage,
  courseName,
  ishideDownloadCurriculum,
  ishideStarRating,
  urlQuery,
  isHpePage,
  customPoints,
}: CourseCardProps) => {
  const [ismodalOpen, setModalOpen] = useState(false)
  const [videoURL, setVideoURL] = useState('')

  const coursesData = COURSE_FIRST_FOLD[courseName]

  const imageProps = {
    SCRATCH_PROGRAMMING: {
      path: `${IMAGE_PATH}scratch-programming/card-image`,
      altText: 'scratch programming',
    },
    PYTHON_FOR_KIDS: {
      path: `${IMAGE_PATH}python-for-kids/card-image`,
      altText:
        'Online coding course for kids to get started with Python programming',
    },
    ROBLOX: {
      path: `${IMAGE_PATH}roblox/card-image`,
      altText:
        'Online Roblox Game Development course for kids to get started with Coding',
    },
    AP_COMPUTER_SCIENCE_A: {
      path: `${IMAGE_PATH}ap-computer-science-a/card-image`,
      altText:
        'Online AP Computer Science A course for kids with Coding Instructor',
    },
    HPE_CODEWARS_CODE_BATTLE: {
      path: `${IMAGE_PATH}hpe-codewars-code-battle/card-image`,
      altText:
        'hpe codewars code battle course for kids with Coding Instructor',
    },
    HPE_CODEWARS_HACKATHON: {
      path: `${IMAGE_PATH}hpe-codewars-hackathon/card-image`,
      altText: 'hpe codewars Hackathon course for kids with Coding Instructor',
    },
    AI_AND_ML: {
      path: `${IMAGE_PATH}ai-and-ml/card-image`,
      altText: 'AI and ML course for kids with Coding Instructor',
    },
    THUNKABLE: {
      path: `${IMAGE_PATH}thunkable/card-image`,
      altText: 'Thunkable course for kids with Coding Instructor',
    },
    JAVA: {
      path: `${IMAGE_PATH}java/card-image`,
      altText: 'Java course for kids with Coding Instructor',
    },
    TRINKET: {
      path: `${IMAGE_PATH}trinket/card-image`,
      altText: 'Trinket course for kids with Coding Instructor',
    },
    PYGAME: {
      path: `${IMAGE_PATH}pygame/card-image`,
      altText: 'Pygame course for kids with Coding Instructor',
    },
    ANIMATION: {
      path: `${IMAGE_PATH}animation/card-image`,
      altText: 'Animation course for kids with Coding Instructor',
    },
  }

  return (
    <>
      <Modal
        isOpen={ismodalOpen}
        setIsOpen={setModalOpen}
        isCrossIconShown={true}
        shouldCloseOnEsc
      >
        <video
          controlsList="nodownload"
          controls
          autoPlay
          className="-my-5 sm:-my-10 rounded-lg mx-auto"
          style={{ height: '80vh' }}
        >
          <source src={videoURL} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </Modal>
      <div className="flex bg-white flex-col md:shadow-lg relative md:w-80 rounded-t-md">
        <FixedMobileCTA title={COURSE_TITLES[courseName]} urlQuery={urlQuery} />

        {!shouldHideCardImage && (
          <>
            {coursesData?.videoURL && (
              <div
                onClick={() => {
                  setModalOpen(true)
                  setVideoURL(coursesData.videoURL)
                }}
              >
                <FastlyImage
                  src={`${imageProps[courseName].path}.png`}
                  webpSrc={`${imageProps[courseName].path}.webp`}
                  alt={imageProps[courseName].altText}
                  width={450}
                  height={252}
                  priority
                  className="cursor-pointer hover:shadow-lg transition-shadow rounded-t-md duration-300"
                />
              </div>
            )}

            {!coursesData?.videoURL && (
              <FastlyImage
                src={`${imageProps[courseName].path}.png`}
                webpSrc={`${imageProps[courseName].path}.webp`}
                alt={imageProps[courseName].altText}
                width={450}
                height={252}
                priority
              />
            )}
          </>
        )}

        <div className="flex flex-col m-5 text-grey text-sm">
          <div className="flex md:hidden">
            <div className="md:w-4/5 md:pr-4 max-w-3xl">
              <h2 className="font-700 text-2xl text-left leading-tight">
                {coursesData.name}
              </h2>

              {!isHpePage && (
                <>
                  <p className="font-600 text-left mb-0 mt-4 max-w-2xl text-base">
                    {coursesData.description}
                  </p>
                  <p className="font-600 text-left my-5 max-w-4xl text-base">
                    {coursesData.modules} Modules, {coursesData.lessons}{' '}
                    Lessons, {coursesData.activities} Activities & Projects,{' '}
                    {coursesData.quizzes} Quizzes
                  </p>
                </>
              )}
              {!ishideStarRating && (
                <div className="flex flex-col md:flex-row md:space-x-8 space-y-5 md:space-y-0 justify-center items-center lg:justify-start">
                  <StudentCountriesOverview isTextWhite={false} />
                  <Ratings isDarkText />
                </div>
              )}
            </div>
          </div>

          <BookFreeClassBtn
            className="text-base lg:block hidden mt-8 lg:mt-0"
            query={urlQuery}
          />
          {coursesData?.curriculumPDFLink && !ishideDownloadCurriculum && (
            <>
              <a
                href={coursesData.curriculumPDFLink}
                target="_blank"
                rel="noreferrer"
                className="hidden lg:flex text-orange mt-3 items-center justify-center"
              >
                Download Curriculum <Download size={14} className="ml-2" />
              </a>
              <a
                href={coursesData.curriculumPDFLink}
                target="_blank"
                rel="noreferrer"
                className="lg:hidden pt-8 md:pt-0"
              >
                <Button
                  className="flex items-center justify-center w-full"
                  variant="outline"
                  size="lg"
                >
                  Download Curriculum <Download size={14} className="ml-2" />
                </Button>
              </a>
            </>
          )}

          <p className="text-lg font-700 mt-5">This course includes:</p>
          <div className="space-y-2 mt-3">
            <div className="flex items-start space-x-2">
              <div className="mt-0">
                <FastlyImage
                  src={`${IMAGE_PATH}icons/session.png`}
                  width={20}
                  height={20}
                  alt="session"
                />
              </div>
              <p>
                {coursesData.lessons ? `${coursesData.lessons} live` : 'Live'}{' '}
                private 1-on-1 sessions of 45 mins each{' '}
              </p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="mt-1">
                <FastlyImage
                  src={`${IMAGE_PATH}icons/learn.png`}
                  width={20}
                  height={20}
                  alt="learn"
                />
              </div>
              <p>Learn live with expert Computer Science teacher</p>
            </div>
            <div className="flex items-center space-x-2">
              <FastlyImage
                src={`${IMAGE_PATH}icons/modules.png`}
                width={20}
                height={20}
                alt="modules"
              />
              <p>
                {coursesData.modules &&
                  `${coursesData.modules} detailed modules`}
              </p>
            </div>

            {coursesData.activities && (
              <div className="flex items-center space-x-2">
                <FastlyImage
                  src={`${IMAGE_PATH}icons/activity.png`}
                  width={20}
                  height={20}
                  alt="activity"
                />
                <p>{coursesData.activities} activities & projects</p>
              </div>
            )}

            {customPoints && (
              <div className="flex items-center space-x-2">
                <FastlyImage
                  src={`${IMAGE_PATH}icons/project.png`}
                  width={20}
                  height={20}
                  alt="certificate"
                />
                <p>{customPoints}</p>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <FastlyImage
                src={`${IMAGE_PATH}icons/certificate.png`}
                width={20}
                height={20}
                alt="certificate"
              />
              <p>Stem.org accredited certificate</p>
            </div>
            <div className="flex items-center space-x-2">
              <FastlyImage
                src={`${IMAGE_PATH}icons/certificate.png`}
                width={20}
                height={20}
                alt="certificate"
              />
              <p>{coursesData.certificate}</p>
            </div>
            <div className="flex items-center space-x-2">
              <FastlyImage
                src={`${IMAGE_PATH}icons/infinity.png`}
                width={20}
                height={20}
                alt="infinity"
              />
              <p>Lifetime access to course content</p>
            </div>
            {!isHpePage && (
              <div className="flex items-start space-x-2">
                <div className="mt-1">
                  <FastlyImage
                    src={`${IMAGE_PATH}icons/personalized.png`}
                    width={20}
                    height={20}
                    alt="personalized"
                  />
                </div>
                <p>Unlimited 1:1 live doubt sessions with instructors</p>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <FastlyImage
                src={`${IMAGE_PATH}icons/users.png`}
                width={20}
                height={20}
                alt="download"
              />
              <p>Live 1:1 class with Coding Instructor</p>
            </div>
            {!isHpePage && (
              <div className="flex items-center space-x-2">
                <FastlyImage
                  src={`${IMAGE_PATH}icons/file.png`}
                  width={20}
                  height={20}
                  alt="cup"
                />
                <p>Receive monthly progress reports</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default CourseCard
